import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserMinus, Clock, Mail, Building2, RefreshCw, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserInvitationStatus } from '@/hooks/useUserInvitationStatus';
import { useReactivationRequests } from '@/hooks/useReactivationRequests';
import { format } from 'date-fns';
import InvitationLinkHandler from '@/components/auth/InvitationLinkHandler';
import ReactivationRequestCard from '@/components/reactivation/ReactivationRequestCard';

interface EnhancedDeactivatedAccountScreenProps {
  deactivatedAt?: string | null;
  deactivationReason?: string | null;
}

const EnhancedDeactivatedAccountScreen: React.FC<EnhancedDeactivatedAccountScreenProps> = ({
  deactivatedAt,
  deactivationReason
}) => {
  const { signOut } = useAuth();
  
  const { 
    pendingInvitations, 
    isLoading: invitationLoading, 
    acceptInvitation,
    refetch: refetchInvitations
  } = useUserInvitationStatus();
  
  const {
    deactivatedMemberships,
    isLoading: reactivationLoading,
    submitReactivationRequest,
    refetch: refetchReactivations
  } = useReactivationRequests();

  useEffect(() => {
    document.title = 'Account Deactivated | Doyence Estimating';
  }, []);

  const handleSignOut = () => {
    signOut();
  };

  const formatDeactivationDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy HH:mm');
    } catch { return dateString; }
  };

  const effectiveDeactivatedAt = deactivatedAt;
  const effectiveDeactivationReason = deactivationReason || "Account has been deactivated by an administrator";

  const refetch = async () => {
    await Promise.all([refetchInvitations(), refetchReactivations()]);
  };

  const hasRecoveryOptions = pendingInvitations.length > 0 || deactivatedMemberships.length > 0;

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center px-4 py-12">
      <header className="sm:mx-auto sm:w-full sm:max-w-2xl text-center mb-6">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
          <UserMinus className="h-6 w-6 text-orange-600" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Account Deactivated</h1>
        <p className="mt-2 text-muted-foreground">
          Your account has been deactivated and you cannot access the system at this time.
        </p>
        
        {effectiveDeactivationReason && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg max-w-md mx-auto">
            <p className="text-sm font-medium text-foreground mb-1">Reason:</p>
            <p className="text-sm text-muted-foreground">{effectiveDeactivationReason}</p>
          </div>
        )}

        {effectiveDeactivatedAt && (
          <div className="mt-3 flex items-center justify-center text-sm text-muted-foreground">
            <Clock className="h-4 w-4 mr-2" />
            <span>Deactivated on: {formatDeactivationDate(effectiveDeactivatedAt)}</span>
          </div>
        )}

        <div className="mt-3 flex items-center justify-center text-sm text-muted-foreground">
          <Mail className="h-4 w-4 mr-2" />
          <span>Contact your administrator to reactivate your account</span>
        </div>
      </header>

      <main className="sm:mx-auto sm:w-full sm:max-w-4xl space-y-6">
        {/* Pending Invitations Section */}
        {pendingInvitations.length > 0 && (
          <section className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              New Company Invitations Available
            </h2>
            <p className="text-muted-foreground mb-4">
              You have pending invitations to join other companies. Accepting an invitation will give you access to that company's system.
            </p>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-medium">{invitation.company_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Invited on {format(new Date(invitation.created_at), 'dd MMMM yyyy')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Expires on {format(new Date(invitation.expires_at), 'dd MMMM yyyy')}
                    </p>
                  </div>
                  <Button 
                    onClick={() => acceptInvitation(invitation.id)}
                    disabled={invitationLoading}
                    className="ml-4"
                  >
                    Accept Invitation
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Reactivation Requests Section */}
        {deactivatedMemberships.length > 0 && (
          <section className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              Request Reactivation
            </h2>
            <p className="text-muted-foreground mb-4">
              You can request to be reactivated in companies where you previously had access.
            </p>
            <div className="space-y-4">
              {deactivatedMemberships.map((membership) => (
                <ReactivationRequestCard
                  key={membership.id}
                  membership={membership}
                  onSubmitRequest={submitReactivationRequest}
                  isLoading={reactivationLoading}
                />
              ))}
            </div>
          </section>
        )}

        {/* Main Actions Section */}
        <section className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Alternative Options
          </h2>
          
          <div className="space-y-4">
            <p className="text-muted-foreground">
              {hasRecoveryOptions 
                ? "You can also join a different company or create your own company while waiting for reactivation."
                : "Since you don't currently have access to any companies, you can join an existing company or create your own."
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <InvitationLinkHandler className="w-full" variant="default" />
              <Link to="/company-creation" className="sm:w-auto w-full">
                <Button className="w-full" variant="outline">Create New Company</Button>
              </Link>
              <Button 
                onClick={handleSignOut}
                className="sm:w-auto w-full" 
                variant="secondary"
              >
                Sign Out
              </Button>
            </div>

            {/* Help Section */}
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <h3 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Info className="h-4 w-4" />
                Need Help?
              </h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p><strong>Join with invitation link:</strong> Use this if you received an invitation URL via email.</p>
                <p><strong>Create new company:</strong> Start fresh by setting up your own company profile.</p>
                <p><strong>Request reactivation:</strong> Ask your previous company administrator to reactivate your access.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default EnhancedDeactivatedAccountScreen;